import { useEffect, useCallback } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { X, ChevronLeft, ChevronRight } from "lucide-react";

const EASE = [0.76, 0, 0.24, 1];

export const Lightbox = ({ images, index, onClose, onPrev, onNext }) => {
    const open = index !== null && index >= 0;

    const handleKey = useCallback(
        (e) => {
            if (!open) return;
            if (e.key === "Escape") onClose();
            if (e.key === "ArrowLeft") onPrev();
            if (e.key === "ArrowRight") onNext();
        },
        [open, onClose, onPrev, onNext],
    );

    useEffect(() => {
        if (open) {
            document.body.style.overflow = "hidden";
        } else {
            document.body.style.overflow = "";
        }
        window.addEventListener("keydown", handleKey);
        return () => {
            document.body.style.overflow = "";
            window.removeEventListener("keydown", handleKey);
        };
    }, [open, handleKey]);

    return (
        <AnimatePresence>
            {open && (
                <motion.div
                    data-testid="lightbox-overlay"
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 1 }}
                    exit={{ opacity: 0 }}
                    transition={{ duration: 0.35, ease: EASE }}
                    onClick={onClose}
                    className="fixed inset-0 z-[100] bg-[var(--ink)]/95 backdrop-blur-md flex items-center justify-center p-6 md:p-16"
                >
                    {/* Top bar */}
                    <div
                        className="absolute top-0 left-0 right-0 flex items-center justify-between px-6 md:px-10 py-5 text-white/80 font-body text-[11px] tracking-[0.32em] uppercase"
                        onClick={(e) => e.stopPropagation()}
                    >
                        <span data-testid="lightbox-counter">
                            {String(index + 1).padStart(2, "0")} /{" "}
                            {String(images.length).padStart(2, "0")}
                        </span>
                        <span className="hidden sm:inline">
                            Galeria · Novo Horizonte
                        </span>
                        <button
                            type="button"
                            onClick={onClose}
                            data-testid="lightbox-close"
                            aria-label="Fechar"
                            className="inline-flex h-10 w-10 items-center justify-center border border-white/25 hover:border-[var(--sun)] hover:text-[var(--sun)] transition-colors"
                        >
                            <X size={18} />
                        </button>
                    </div>

                    {/* Prev button */}
                    <button
                        type="button"
                        onClick={(e) => {
                            e.stopPropagation();
                            onPrev();
                        }}
                        data-testid="lightbox-prev"
                        aria-label="Anterior"
                        className="absolute left-2 md:left-8 top-1/2 -translate-y-1/2 h-12 w-12 md:h-14 md:w-14 inline-flex items-center justify-center border border-white/25 text-white/85 hover:border-[var(--sun)] hover:text-[var(--sun)] transition-colors bg-black/20"
                    >
                        <ChevronLeft size={22} />
                    </button>

                    {/* Image with motion */}
                    <motion.img
                        key={index}
                        initial={{ opacity: 0, scale: 0.96 }}
                        animate={{ opacity: 1, scale: 1 }}
                        exit={{ opacity: 0, scale: 0.96 }}
                        transition={{ duration: 0.45, ease: EASE }}
                        onClick={(e) => e.stopPropagation()}
                        src={images[index]}
                        alt={`Foto ${index + 1} — ampliada`}
                        data-testid="lightbox-image"
                        className="max-h-[85vh] max-w-[92vw] md:max-w-[80vw] object-contain shadow-[0_30px_80px_rgba(0,0,0,0.5)]"
                    />

                    {/* Next button */}
                    <button
                        type="button"
                        onClick={(e) => {
                            e.stopPropagation();
                            onNext();
                        }}
                        data-testid="lightbox-next"
                        aria-label="Próxima"
                        className="absolute right-2 md:right-8 top-1/2 -translate-y-1/2 h-12 w-12 md:h-14 md:w-14 inline-flex items-center justify-center border border-white/25 text-white/85 hover:border-[var(--sun)] hover:text-[var(--sun)] transition-colors bg-black/20"
                    >
                        <ChevronRight size={22} />
                    </button>

                    {/* Bottom hint */}
                    <div
                        className="absolute bottom-4 left-0 right-0 text-center text-white/50 font-body text-[10px] tracking-[0.32em] uppercase"
                        onClick={(e) => e.stopPropagation()}
                    >
                        Esc · fechar · ← → · navegar
                    </div>
                </motion.div>
            )}
        </AnimatePresence>
    );
};
